import { ApiRequest, ShowLoader } from 'GlobalFunctions';
import { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { initializeSubscription } from 'components/Subscription/Subscription';

import {ProjectConfig} from 'Global';

// Add this new function to calculate the next payment date based on billing cycle
const calculateNextPaymentDate = (billingCycle: string): string => {
    const [timePart] = billingCycle.split(":");
    const qty = parseInt(timePart.slice(0, -1), 10);
    const unit = timePart.slice(-1);
    
    const nextDate = new Date();
    
    switch (unit) {
        case "y": 
            nextDate.setFullYear(nextDate.getFullYear() + qty);
            break;
        case "m": 
            nextDate.setMonth(nextDate.getMonth() + qty);
            break;
        case "w": 
            nextDate.setDate(nextDate.getDate() + (qty * 7));
            break;
        case "d": 
            nextDate.setDate(nextDate.getDate() + qty);
            break;
    }
    
    // Format date as "DD Month, YYYY" - explicitly use en-US locale to ensure English month names
    const day = nextDate.getDate();
    const month = nextDate.toLocaleString('en-US', { month: 'long' });
    const year = nextDate.getFullYear();
    
    return `${day} ${month}, ${year}`;
};

// Update the next payment date display
const updateNextPaymentDate = (billingCycle: string): void => {
    const nextPaymentElement = document.querySelector('[pc="next-payment"]');
    if (nextPaymentElement) {
        const nextDate = calculateNextPaymentDate(billingCycle);
        nextPaymentElement.textContent = `Next payment ${nextDate}`;
    }
};

/*=========================================================
GET SUBSCRIPTION TEMPLATE
=========================================================*/
const getSubscriptionTemplate = async (): Promise<string> => {
    let templateHTML = '';
    
    await ApiRequest({
        url: "/templates/getTemplate",
        method: "get",
        query: {
            template_name: "subscription",
        },
        setResponse: (response: any) => {
            if (response.status === 'success' && response.template) {
                templateHTML = response.template;
            } else {
                console.error("Failed to load subscription template:", response);
            }
        }
    });
    
    return templateHTML;
}


const handleClose = (event?: MouseEvent) => {
  const popup = document.querySelector('[pc="subscription"]') as HTMLElement;
  // If event is provided, only close when clicking outside the inner container
  if (event) {
    const popupInner = document.querySelector('[pc="subscription-wrapper"]') as HTMLElement;
    if (popup && popupInner && !popupInner.contains(event.target as Node)) {
      popup.style.display = 'none';
      document.removeEventListener('click', handleClose);
    }
  } else {
    if (popup) {
      popup.style.display = 'none';
      document.removeEventListener('click', handleClose);
    }
  }
};

/**
 * Inserts subscription HTML content based on the subscription mode
 * @param {string} subscriptionHTML - The HTML template to insert
 * @param {string} subscriptionMode - The subscription mode (embed or modal)
 * @returns {HTMLElement|null} - The inserted element or null if insertion failed
 */
const insertSubscriptionContent = (subscriptionHTML: string, subscriptionMode: string): HTMLElement | null => {
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = subscriptionHTML;
    const subscriptionElement = tempContainer.querySelector('[pc="subscription"]') as HTMLElement;
    
    if (!subscriptionElement) return null;
    
    if (subscriptionMode === 'embed') {
        // Hide the add button if it exists
        const addBtn = document.querySelector('[pc="add-btn"]') as HTMLElement;
        if (addBtn) {
            addBtn.style.display = 'none';
        }
        const wrapper = document.querySelector('[pc="sub-embed-container"]');
        if (wrapper) {
            // Only embed the .subscription-wrapper content
            const popupInner = subscriptionElement.querySelector('[pc="subscription-wrapper"]');
            if (popupInner) {
                wrapper.innerHTML = '';
                wrapper.appendChild(popupInner);
                return popupInner as HTMLElement;
            }
            console.error("subscription-wrapper not found for embed mode");
            return null;
        }
        console.error("Subscription wrapper not found for embed mode");
        return null;
    } else {
        // Default: append to body (modal mode)
        // Set display: none immediately after obtaining the template
        subscriptionElement.style.display = 'none';
        subscriptionElement.style.position = 'fixed';
        subscriptionElement.style.top = '0';
        subscriptionElement.style.width = '100%';
        subscriptionElement.style.height = '100%';
        subscriptionElement.style.zIndex = '9999';


        document.body.appendChild(subscriptionElement);
        return subscriptionElement;
    }
};

/**
 * Check if the current product is a subscription type
 * @returns {boolean} True if the product is a subscription
 */
export const checkForSubscription = async () => {

            console.log("CHECKING FOR SUBSCRIPTION");
    // Check if the subscription form element exists
    const subscriptionFormEl = document.querySelector('[pc-sub="form"]');
    if (subscriptionFormEl) {
        // Determine subscription mode (default to modal if not specified)
        const subscriptionMode = subscriptionFormEl.getAttribute('pc-sub-mode') || 'modal';
        
        // For modal mode, check if popup already exists
        if (subscriptionMode !== 'embed' && document.querySelector('[pc="subscription"]')) {
            return true;
        }
        
        // For embed mode, check if content is already in wrapper
        if (subscriptionMode === 'embed' && document.querySelector('[pc="subscription-wrapper"] > *')) {
            return true;
        }
        
        // Get the HTML content from the API
        const subscriptionPopupHTML = await getSubscriptionTemplate();
        
        // Insert the content based on the mode
        const subscriptionElement = insertSubscriptionContent(subscriptionPopupHTML, subscriptionMode);
        if (subscriptionElement) {
            console.log(`Added subscription content in ${subscriptionMode} mode`);

            // Hide subscription status element initially and ensure form is visible
            const subscriptionStatusEl = subscriptionElement.querySelector('[pc="subscription-status"]') as HTMLElement;
            const subscriptionFormEl = subscriptionElement.querySelector('[pc="subscription-form"]') as HTMLElement;
            
            if (subscriptionStatusEl) {
                subscriptionStatusEl.classList.add("hide_content");
            }
            if (subscriptionFormEl) {
                subscriptionFormEl.classList.remove("hide_content");
            }

            // Get billing cycle data from pc-sub-value element
            const billingCycleDataEl = document.querySelector('[pc-sub-value]');
            const billingCycleSelectEl = document.querySelector('[pc-sub-element]') as HTMLSelectElement;
            
            if (billingCycleDataEl && billingCycleSelectEl && billingCycleDataEl.textContent) {
                // Hide the billing cycle data element
                (billingCycleDataEl as HTMLElement).style.display = 'none';
                
                const cycles = billingCycleDataEl.textContent.split(",").map(str => str.trim());
                
                // Clear existing options
                billingCycleSelectEl.innerHTML = "";
                
                cycles.forEach(cycle => {
                    const [timePart, costPart] = cycle.split(":");
                    const qty = parseInt(timePart.slice(0, -1), 10);
                    const unit = timePart.slice(-1);
                    let label = "";
                    switch (unit) {
                        case "y": label = qty === 1 ? "Annually" : `${qty} years`; break;
                        case "m": label = qty === 1 ? "Monthly" : `${qty} months`; break;
                        case "w": label = qty === 1 ? "Weekly" : `${qty} weeks`; break;
                        case "d": label = qty === 1 ? "Daily" : `${qty} days`; break;
                    }
                    const option = document.createElement("option");
                    option.value = cycle;
                    option.text = `${label} ($${costPart})`;
                    billingCycleSelectEl.appendChild(option);
                });

                // Preselect first item and update price
                if (cycles.length > 0) {
                    billingCycleSelectEl.selectedIndex = 0;
                    const [, firstCostPart] = cycles[0].split(":");
                    const firstCostNum = parseFloat(firstCostPart).toFixed(2);
                    const summaryPrice = document.querySelector('[pc="summary-price"]');
                    const recurrentAmount = document.querySelector('[pc="recurrent-amount"]');
                    if (summaryPrice) summaryPrice.textContent = `$${firstCostNum}`;
                    if (recurrentAmount) recurrentAmount.textContent = `$${firstCostNum}`;
                    const productPriceEl = document.querySelector('[pc="product-price"]');
                    if (productPriceEl) productPriceEl.textContent = `${firstCostNum}`;
                    
                    // Initialize next payment date with first cycle
                    updateNextPaymentDate(cycles[0]);
                }

                // Add event listener to update price
                billingCycleSelectEl.addEventListener("change", () => {
                    const [, costPart] = billingCycleSelectEl.value.split(":");
                    const costNum = parseFloat(costPart).toFixed(2);
                    const summaryPrice = document.querySelector('[pc="summary-price"]');
                    const recurrentAmount = document.querySelector('[pc="recurrent-amount"]');
                    if (summaryPrice) summaryPrice.textContent = `$${costNum}`;
                    if (recurrentAmount) recurrentAmount.textContent = `$${costNum}`;

                    // Also update product-price
                    const productPriceEl = document.querySelector('[pc="product-price"]');
                    if (productPriceEl) productPriceEl.textContent = `${costNum}`;
                    
                    // Update next payment date based on new cycle
                    updateNextPaymentDate(billingCycleSelectEl.value);
                    
                    // Update localStorage with new billing cycle and price (important for embed mode)
                    const storedData = localStorage.getItem('subscription');
                    if (storedData) {
                        try {
                            const subscriptionData = JSON.parse(storedData);
                            
                            // Update billing cycle in the first product
                            if (subscriptionData.products && subscriptionData.products[0]) {
                                subscriptionData.products[0].billingCycle = billingCycleSelectEl.value;
                                subscriptionData.products[0].product_price = costNum;
                            }
                            
                            // Update subtotal and total_price
                            subscriptionData.subtotal = parseFloat(costNum);
                            subscriptionData.total_price = parseFloat(costNum);
                            
                            // Save updated data back to localStorage
                            localStorage.setItem('subscription', JSON.stringify(subscriptionData));
                            
                            console.log('Updated subscription data in localStorage:', subscriptionData);
                        } catch (error) {
                            console.error('Error updating subscription data in localStorage:', error);
                        }
                    }
                });
            }

            // Add event listeners for popup (only in modal mode)
            if (subscriptionMode !== 'embed') {
                const closeButton = document.querySelector('.subscription-close-btn');
                const cancelButton = document.querySelector('[pc="cancel-subscription"]');
                
                if (closeButton) {
                    closeButton.addEventListener("click", () => {
                        const popup = document.querySelector('[pc="subscription"]') as HTMLElement;
                        if (popup) {
                            popup.style.display = "none";
                        }
                    });
                }
                
                if (cancelButton) {
                    cancelButton.addEventListener("click", () => {
                        const popup = document.querySelector('[pc="subscription"]') as HTMLElement;
                        if (popup) {
                            popup.style.display = "none";
                        }
                    });
                }
            }
        }
        return true;
    }
    return false;
};

/**
 * Starts the subscription process for a product
 * @param {any} productData Product data for subscription
 */
export const startSubscription = async (productData: any) => {

    localStorage.removeItem('subscription');
    localStorage.removeItem('shippingMethods');
    // Optionally clear other related keys if needed
    // localStorage.removeItem('order-id'); // Uncomment if you want to reset order id

    // Determine subscription mode
    const subscriptionFormEl = document.querySelector('[pc-sub="form"]');
    const subscriptionMode = subscriptionFormEl?.getAttribute('pc-sub-mode') || 'modal';

    let subscriptionElement: HTMLElement | null = null;
    
    if (subscriptionMode === 'embed') {
        // For embed mode, get or insert content in the wrapper
        let wrapper = document.querySelector('[pc="subscription-wrapper"]');
        if (!wrapper || !wrapper.hasChildNodes()) {
            // Get the HTML content from the API
            const subscriptionHTML = await getSubscriptionTemplate();
            subscriptionElement = insertSubscriptionContent(subscriptionHTML, 'embed');
        } else {
            subscriptionElement = wrapper.firstElementChild as HTMLElement;
        }
    } else {
        // For modal mode, handle popup display
        let popup = document.querySelector('[pc="subscription"]') as HTMLElement;
        if (!popup) {
            // Get the HTML content from the API
            const subscriptionHTML = await getSubscriptionTemplate();
            subscriptionElement = insertSubscriptionContent(subscriptionHTML, 'modal');
            popup = subscriptionElement as HTMLElement;
        } else {
            subscriptionElement = popup;
        }
        
        // Show the popup for modal mode
        if (popup) {
            popup.style.display = "flex";
            
            setTimeout(() => {
                document.addEventListener('click', handleClose);
            }, 1000);


            //configure close button
            const closeButtonEl = document.querySelector('[pc="close"]');
            if (closeButtonEl) {
                closeButtonEl.addEventListener('click', (event) => {
                    event.stopPropagation(); // Prevent the click from bubbling up to the document
                    handleClose();
                });
            }

            const backgroundCloseEl = document.querySelector('[pc="subscription-background"]');
            if (backgroundCloseEl) {
                backgroundCloseEl.addEventListener('click', (event) => {
                    event.stopPropagation(); // Prevent the click from bubbling up to the document
                    handleClose();
                });
            }
        }
    }
    
    if (subscriptionElement) {
        
        /* SHOW COUPON FORM AGAIN
        ================================================*/
        const coupon_form = document.querySelector('[pc="discount-form"]');
        coupon_form?.classList.remove('hidden');
         // remove pc="active-coupon" 
        const activeCouponEl = document.querySelector('[pc="active-coupon"]');
        if (activeCouponEl) {
            activeCouponEl.remove();
        }
        /*================================================*/

        // --- Clear all form fields in the subscription content ---
        const formContainer = subscriptionElement.querySelector('[pc="subscription-form"]');
        if (formContainer) {
            // Find all input, textarea, and select elements within the form container
            const inputElements = formContainer.querySelectorAll('input, textarea, select');
            inputElements.forEach((el: any) => {
                if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
                    if (el instanceof HTMLInputElement && (el.type === 'checkbox' || el.type === 'radio')) {
                        el.checked = false;
                    } else if (el instanceof HTMLInputElement && el.type === 'submit') {
                        // Skip submit buttons - don't clear their value as it contains the button text
                        return;
                    } else {
                        el.value = '';
                    }
                } else if (el instanceof HTMLSelectElement) {
                    el.selectedIndex = 0;
                }
            });
        }

        // Hide subscription status element and ensure subscription form is visible
        const subscriptionStatusEl = subscriptionElement.querySelector('[pc="subscription-status"]') as HTMLElement;
        const subscriptionFormEl = subscriptionElement.querySelector('[pc="subscription-form"]') as HTMLElement;
        
        if (subscriptionStatusEl) {
            subscriptionStatusEl.classList.add("hide_content");
        }
        if (subscriptionFormEl) {
            subscriptionFormEl.classList.remove("hide_content");
        }

        // --- Reset summary price fields ---
        const summaryPrice = subscriptionElement.querySelector('[pc="summary-price"]');
        if (summaryPrice) summaryPrice.textContent = '$0.00';
        const summaryTotal = subscriptionElement.querySelector('[pc="recurrent-amount"]');
        if (summaryTotal) summaryTotal.textContent = '$0.00';

        // --- Continue with normal logic ---
        // Update summary with product data
        if (summaryPrice && productData.product_price) {
            console.log("Updating summary price with product data");
            summaryPrice.textContent = `$${parseFloat(productData.product_price).toFixed(2)}`;
        }
        if (summaryTotal && productData.product_price) {
            summaryTotal.textContent = `$${parseFloat(productData.product_price).toFixed(2)}`;
        }

        // Update product name in subscription popup
        const productNameEl = document.querySelector('[pc="product-name"]');
        const subProductNameEl = subscriptionElement.querySelector('[pc="product-name"]');
        const closeButtonEl = subscriptionElement.querySelector('[pc="close"]');
        
        if (subscriptionMode === 'embed') {
            console.log("Updating product name in embed mode");
            if (subProductNameEl) {
                (subProductNameEl as HTMLElement).style.display = 'none';
            }
            if (closeButtonEl) {
                (closeButtonEl as HTMLElement).style.display = 'none';
            }
        } else {
            // Update product name in modal mode
            if (productNameEl && subProductNameEl) {
                subProductNameEl.textContent = productNameEl.textContent;
            }
        }

        // Get the selected billing cycle and add it to productData
        const billingCycleSelect = document.querySelector('[pc-sub-element]') as HTMLSelectElement;
        if (billingCycleSelect) {
            console.log("Billing cycle select found, updating product data", billingCycleSelect.value);
            productData.billingCycle = billingCycleSelect.value;
            
            // Update next payment date when subscription starts
            updateNextPaymentDate(billingCycleSelect.value);
        }

        // Store product data in the subscription object in localStorage
        // (now always fresh)
        const updatedData = {
            products: [productData]
        };
        localStorage.setItem('subscription', JSON.stringify(updatedData));

        // Get payment form configuration from backend
        await getPaymentForm(productData);
    }
};

/*===============================================
GET PAYMENT FORM
===============================================*/
export const getPaymentForm = async (additionalData: any) => {
    ShowLoader(true);
    await ApiRequest({
        url: "/payments/getSubscriptionPaymentForm",
        method: "get",
        query: {
            orderId: localStorage.getItem('order-id'),
        },
        setResponse: async (response: any) => {
            console.log("Payment form response", response);
            ShowLoader(false);
            if(response.status === 'error') {
                alert(response.message);
                return;
            }
            
            // Store payment method information in the subscription object
            const storedData = localStorage.getItem('subscription');
            let subscriptionData = {};
            
            try {
                if (storedData) {
                    subscriptionData = JSON.parse(storedData);
                }
            } catch (error) {
                console.error('Error parsing subscription data:', error);
            }
            
            // Update subscription with payment info
            const updatedData = {
                ...subscriptionData,
                payment_info: {
                    payment_method: response.payment_method
                }
            };
            
            localStorage.setItem('subscription', JSON.stringify(updatedData));
            
            // Initialize the form (this will set up event listeners and populate fields)
            initializeSubscription();
            
            // If there's any payment method specific configuration needed
            configurePaymentMethod(response.paymentMethod);
        },
    });
};

/*===============================================
CONFIGURE PAYMENT METHOD
===============================================*/
const configurePaymentMethod = (paymentMethod: string) => {
    switch(paymentMethod) {
        case 'elavon':
            // Add any Elavon-specific configurations here
            break;
        case 'stripe':
            // Add any Stripe-specific configurations here
            break;
        case 'authorize_net':
            // Add any Authorize.Net-specific configurations here
            break;
        case 'test_mode':
            // Add test mode indication
            const testModeIndicator = document.createElement('div');
            testModeIndicator.className = 'test-mode-indicator';
            testModeIndicator.innerHTML = '<span>Test Mode - No actual charges will be made</span>';
            
            const paymentForm = document.querySelector('[pc="payment-form"]');
            if (paymentForm) {
                paymentForm.prepend(testModeIndicator);
            }
            break;
        default:
            // Default configuration
            break;
    }
};

/**
 * Cleanup subscription-related DOM elements
 */
export const cleanupSubscription = () => {
    // Remove event listener when cleaning up
    document.removeEventListener('click', handleClose);
    
    // Determine subscription mode
    const subscriptionFormEl = document.querySelector('[pc-sub="form"]');
    const subscriptionMode = subscriptionFormEl?.getAttribute('pc-sub-mode') || 'modal';
    
    if (subscriptionMode === 'embed') {
        // For embed mode, clear the wrapper
        const wrapper = document.querySelector('[pc="subscription-wrapper"]');
        if (wrapper) {
            wrapper.innerHTML = '';
        }
    } else {
        // For modal mode, remove the popup
        const subscriptionPopup = document.querySelector('[pc="subscription"]');
        if (subscriptionPopup) {
            try {
                document.body.removeChild(subscriptionPopup);
            } catch (error) {
                console.warn('Error removing subscription popup:', error);
            }
        }
    }
    
    // Clear subscription data from localStorage
    localStorage.removeItem('subscription');
};

